import { apiClient } from '@/shared/lib/api/client';
import type { Responsavel } from '@/types/api';
import type {
  ChatResumo,
  ConversaDetalhe,
  ConversaFamiliaRequest,
  ChatElegivelColaborador,
  ChatElegivelFamiliar,
  ConversaGrupoRequest,
  ConversaIndividualRequest,
  ConversaResumo,
  EncaminharDestino,
  LeiturasMensagem,
  Mensagem,
  MensagemRequest,
} from './chatTypes';

export const chatService = {
  listarConversas: async (busca?: string): Promise<ConversaResumo[]> => {
    const params =
      busca && busca.trim().length >= 2 ? { busca: busca.trim() } : undefined;
    const { data } = await apiClient.get<ConversaResumo[]>('/v1/chat/conversas', { params });
    return data;
  },

  obterResumo: async (): Promise<ChatResumo> => {
    const { data } = await apiClient.get<ChatResumo>('/v1/chat/resumo');
    return data;
  },

  abrirIndividual: async (payload: ConversaIndividualRequest): Promise<ConversaDetalhe> => {
    const { data } = await apiClient.post<ConversaDetalhe>('/v1/chat/conversas/individuais', payload);
    return data;
  },

  abrirFamilia: async (payload: ConversaFamiliaRequest): Promise<ConversaDetalhe> => {
    const { data } = await apiClient.post<ConversaDetalhe>('/v1/chat/conversas/familia', payload);
    return data;
  },

  criarGrupo: async (payload: ConversaGrupoRequest): Promise<ConversaDetalhe> => {
    const { data } = await apiClient.post<ConversaDetalhe>('/v1/chat/conversas/grupos', payload);
    return data;
  },

  listarColaboradoresElegiveis: async (): Promise<ChatElegivelColaborador[]> => {
    const { data } = await apiClient.get<ChatElegivelColaborador[]>('/v1/chat/elegiveis/colaboradores');
    return data;
  },

  listarFamiliaresElegiveis: async (idsAlunos: number[]): Promise<ChatElegivelFamiliar[]> => {
    const { data } = await apiClient.get<ChatElegivelFamiliar[]>('/v1/chat/elegiveis/familiares', {
      params: { idsAlunos },
      paramsSerializer: { indexes: null },
    });
    return data;
  },

  obterConversa: async (id: number): Promise<ConversaDetalhe> => {
    const { data } = await apiClient.get<ConversaDetalhe>(`/v1/chat/conversas/${id}`);
    return data;
  },

  obterResponsavel: async (id: number): Promise<Responsavel> => {
    const { data } = await apiClient.get<Responsavel>(`/v1/responsaveis/${id}`);
    return data;
  },

  listarMensagens: async (
    idConversa: number,
    params?: { antesDe?: number; tamanho?: number; busca?: string },
  ): Promise<Mensagem[]> => {
    const query: Record<string, string | number> = {};
    if (params?.antesDe != null) query.antesDe = params.antesDe;
    if (params?.tamanho != null) query.tamanho = params.tamanho;
    if (params?.busca && params.busca.trim().length >= 2) {
      query.busca = params.busca.trim();
    }
    const { data } = await apiClient.get<Mensagem[]>(`/v1/chat/conversas/${idConversa}/mensagens`, {
      params: Object.keys(query).length ? query : undefined,
    });
    return data;
  },

  listarMidias: async (
    idConversa: number,
    params?: { antesDe?: number; tamanho?: number; tipo?: number },
  ): Promise<Mensagem[]> => {
    const { data } = await apiClient.get<Mensagem[]>(`/v1/chat/conversas/${idConversa}/midias`, {
      params,
    });
    return data;
  },

  enviarMensagem: async (idConversa: number, payload: MensagemRequest): Promise<Mensagem> => {
    const { data } = await apiClient.post<Mensagem>(
      `/v1/chat/conversas/${idConversa}/mensagens`,
      payload,
    );
    return data;
  },

  editarMensagem: async (idMensagem: number, texto: string): Promise<Mensagem> => {
    const { data } = await apiClient.patch<Mensagem>(`/v1/chat/mensagens/${idMensagem}`, { texto });
    return data;
  },

  reagir: async (idMensagem: number, codigo: number): Promise<Mensagem> => {
    const { data } = await apiClient.post<Mensagem>(`/v1/chat/mensagens/${idMensagem}/reacoes`, {
      codigo,
    });
    return data;
  },

  encaminhar: async (
    idMensagem: number,
    destinos: EncaminharDestino[],
  ): Promise<Mensagem[]> => {
    const { data } = await apiClient.post<Mensagem[]>(
      `/v1/chat/mensagens/${idMensagem}/encaminhar`,
      { destinos },
    );
    return data;
  },

  listarLeituras: async (idMensagem: number): Promise<LeiturasMensagem> => {
    const { data } = await apiClient.get<LeiturasMensagem>(
      `/v1/chat/mensagens/${idMensagem}/leituras`,
    );
    return data;
  },

  ocultarMensagem: async (idMensagem: number): Promise<void> => {
    await apiClient.post(`/v1/chat/mensagens/${idMensagem}/ocultar`);
  },

  marcarLeitura: async (idConversa: number, idMensagem: number): Promise<void> => {
    await apiClient.post(`/v1/chat/conversas/${idConversa}/leitura`, { idMensagem });
  },

  marcarNaoLida: async (idConversa: number): Promise<ConversaResumo> => {
    const { data } = await apiClient.post<ConversaResumo>(
      `/v1/chat/conversas/${idConversa}/nao-lida`,
    );
    return data;
  },

  ocultarConversa: async (idConversa: number): Promise<void> => {
    await apiClient.post(`/v1/chat/conversas/${idConversa}/ocultar`);
  },

  atualizarGrupo: async (
    idConversa: number,
    payload: { titulo?: string; idArquivoFoto?: number },
  ): Promise<ConversaDetalhe> => {
    const { data } = await apiClient.patch<ConversaDetalhe>(
      `/v1/chat/conversas/${idConversa}`,
      payload,
    );
    return data;
  },

  adicionarParticipantes: async (
    idConversa: number,
    payload: {
      idsColaboradores?: number[];
      idsResponsaveis?: number[];
      idsAlunos?: number[];
    },
  ): Promise<ConversaDetalhe> => {
    const { data } = await apiClient.post<ConversaDetalhe>(
      `/v1/chat/conversas/${idConversa}/participantes`,
      payload,
    );
    return data;
  },

  removerParticipante: async (idConversa: number, idColaborador: number): Promise<void> => {
    await apiClient.delete(`/v1/chat/conversas/${idConversa}/participantes/${idColaborador}`);
  },

  removerParticipanteResponsavel: async (
    idConversa: number,
    idResponsavel: number,
  ): Promise<void> => {
    await apiClient.delete(
      `/v1/chat/conversas/${idConversa}/participantes/responsaveis/${idResponsavel}`,
    );
  },

  obterPresenca: async (idsColaborador: number[]): Promise<Record<string, boolean>> => {
    if (idsColaborador.length === 0) return {};
    const qs = idsColaborador.map((id) => `idsColaborador=${id}`).join('&');
    const { data } = await apiClient.get<{ online: Record<string, boolean> }>(
      `/v1/presenca?${qs}`,
    );
    return data.online ?? {};
  },
};
